<template>
    <div v-if="config.display" :style="{'width': config.width || '100%'}" class="control"  v-bind="config" :data-test-id="dataTestId">
        <label-wrapper :label-settings="config"></label-wrapper>
        <div v-if="asField">
            <display-value :id="config.id"><span class="date-time">{{time}}</span></display-value>
        </div>
        <div v-else>
            <div v-if="config.isCalendar" class="date-calendar">
                <ejs-calendar
                    v-bind="syncfusionConfig"
                    v-model="dateValue"
                    :data-test-id="dataTestId"
                    :enabled="!config.disabled"
                    :ref="ref"
                    :change="change"
                    :blur="dateBlur"
                ></ejs-calendar>
            </div>
            <div v-else :class="{'flex': config.allowRelativeDate}">
                <div>
                    <ejs-dropdownlist
                        :readonly="config.readonly || config.disabled"
                        v-if="config.allowRelativeDate"
                        :change='relativeDateChanged'
                        :dataSource="datePeriods"
                        :fields='ddFields'
                        :enabled="!config.disabled"
                        v-model='relativeValue.datePeriod'
                        :value="relativeValue.datePeriod"
                        showClearButton="true"
                    ></ejs-dropdownlist>
                </div>
                <div v-if="showDateInput">
                    <ejs-datepicker
                        v-bind="syncfusionConfig"
                        v-model="dateValue"
                        :data-test-id="dataTestId"
                        :enabled="!config.disabled && value !== '_empty_'"
                        :ref="ref"
                        :created="created"
                        :change="change"
                        :blur="dateBlur"
                        :min="config.minDate"
                        :max="config.maxDate"
                    ></ejs-datepicker>
                    <p v-if="value === '_empty_'" class="empty-field-notice"><span class="fa-solid fa-bomb fa-flip-horizontal" style="margin-right: 3px; font-size: 16px; color: #ad0808"></span>Note: Existing values for this field will be cleared on save. <a @click="value = ''">Undo</a></p>
                </div>
                <div v-if="config.allowRelativeDate && showDayInput">
                    <ejs-dropdownlist :readonly="config.readonly || config.disabled" :change='relativeDateChanged' :dataSource="days" :fields='ddFields' v-model='relativeValue.day'></ejs-dropdownlist>
                </div>
                <div v-if="config.allowRelativeDate && showAmountInput">
                    <ejs-numerictextbox :enabled="!(config.readonly || config.disabled)" format="###" v-model="relativeValue.amount" :min="1" :max="99" :change="relativeDateChanged"></ejs-numerictextbox>
                </div>
                <div v-if="config.allowRelativeDate && showUnitInput">
                    <ejs-dropdownlist :readonly="config.readonly || config.disabled" :change='relativeDateChanged' :dataSource="units" :fields='ddFields' v-model='relativeValue.unit'></ejs-dropdownlist>
                </div>
            </div>
            <p v-if="config.message" class="input-info" v-html="ddStoreFilter(config.message)"></p>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { controlMixins } from "./controlMixins.js";

let editorSettings = {
    onchange: {
        type: 'actionfield',
        label: 'Change Action',
        itemsSource: 'actionStore.getActionOptions()',
        fields: {
            text: 'text',
            value: 'value'
        },
        default: {
            action: '',
            params: {}
        }
    }
};

export const dateComponent = {
    editorSettings: editorSettings,
    data: function () {
        return {
            time: "",
            datePeriods: [
                { text: 'specific', value: 0 },
                { text: 'yesterday', value: 101 },
                { text: 'today', value: 102 },
                { text: 'tomorrow', value: 103 },
                { text: 'today minus', value: 90 },
                { text: 'today plus', value: 91 },
                { text: 'first day of', value: 146 },
                { text: 'last day of', value: 147 },
                { text: 'day (number)', value: 148 }, // week, month, quarter, year
                { text: 'day (Mon-Sun)', value: 149 }, // week, month, quarter, year
            ],
            ddFields: {
                text: 'text',
                value: 'value'
            },
            localRelativeValue: {
                datePeriod: 0,
                amount: null,
                day: null,
                unit: 'days',

            }
        };
    },
    mixins: [controlMixins],
    updated() {
        this.addKeyUpHandler();
    },
    methods: {
        created() {
            if (this.config) {
                this.addKeyUpHandler();
            }
        },
        addKeyUpHandler() {
            if (typeof this?.$refs?.[this.ref]?.ej2Instances?.element?.parentElement?.addEventListener === 'function') {
                this.$refs[this.ref].ej2Instances.element.parentElement.addEventListener('keyup', this.keyup);
            }
        },
        async keyup(ev) {
            if (ev.keyCode === 13) { // enter
                if (ev?.srcElement?.value === '_empty_') this.value = '_empty_';
                if (this.valueChangeTimeout) {
                    await new Promise((resolve) => {
                        let stopWatch = this.$watch('valueChangeTimeout', (valueChangeTimeout) => {
                            if (valueChangeTimeout === 0) {
                                stopWatch();
                                resolve();
                            }
                        });
                    });
                }
                if (typeof this.$parent.processAction === 'function') { // NOSONAR
                    this.$parent.processAction('trapEnter', ev);
                } else if (this.$parent.component && typeof this.$parent.component.processAction === 'function') {
                    this.$parent.component.processAction('trapEnter', ev);
                }
            }
        },
        dateBlur(event) {
            if (event?.model?.element?.value === '_empty_') this.value = '_empty_';
        },
        change: function (event) {
            this.action(event, 'onchange');
        },
        timeTick: function () {
            let time = moment();
            let addHours = parseInt(this.config.addHours, 10) || 0;
            if (addHours) {
                time.add(addHours, "hours");
            }
            let addMinutes = parseInt(this.config.addMinutes, 10) || 0;
            if (addMinutes) {
                time.add(addMinutes, "minutes");
            }
            this.time = time.format(this.config.format);
        },
        relativeDateChanged: function () {
            if (this.localRelativeValue.datePeriod !== 0) {
                if (!this.showAmountInput) {
                    this.localRelativeValue.amount = null;
                }
                if (!this.showUnitInput) {
                    this.localRelativeValue.unit = 'days';
                }
                if (!this.showDayInput) {
                    this.localRelativeValue.day = null;
                }
                this.value = JSON.stringify(this.localRelativeValue);
            }
        }
    },
    mounted: function () {
        if (this.asField) {
            this.timeTick();
            setInterval(this.timeTick, 1000); // NOSONAR
        }
    },
    computed: {
        days() {
            if (this.relativeValue) {
                if (this.relativeValue.datePeriod === 148) {
                    let tmpDays = [];
                    let daysInCurrentMonth = 31;
                    if (parseInt(this.relativeValue.unit, 10) > 0) {
                        daysInCurrentMonth = library.getDaysInMonth(parseInt(this.relativeValue.unit, 10));
                    }
                    for (let i = 1; i <= daysInCurrentMonth; i++) {
                        let tmpText = i;
                        if ([1, 21, 31].indexOf(i) !== -1) tmpText += 'st';
                        else if ([2, 22].indexOf(i) !== -1) tmpText += 'nd';
                        else if ([3, 23].indexOf(i) !== -1) tmpText += 'rd';
                        else tmpText += 'th';
                        tmpDays.push({
                            text: tmpText,
                            value: i
                        });
                    }
                    return tmpDays;
                }
                if (this.relativeValue.datePeriod === 149) {
                    return [
                        { text: 'Sunday', value: 6 },
                        { text: 'Monday', value: 0 },
                        { text: 'Tuesday', value: 1 },
                        { text: 'Wednesday', value: 2 },
                        { text: 'Thursday', value: 3 },
                        { text: 'Friday', value: 4 },
                        { text: 'Saturday', value: 5 },
                    ];
                }
            }
            return [];
        },
        units() {
            if (this.relativeValue) {
                if (this.relativeValue.datePeriod === 90 || this.relativeValue.datePeriod === 91) {
                    return [
                        { text: 'day(s)', value: 'days' },
                        { text: 'week(s)', value: 'weeks' },
                        { text: 'month(s)', value: 'months' },
                        { text: 'quarter(s)', value: 'quarters' },
                        { text: 'year(s)', value: 'years' },
                    ];
                }
                if (this.relativeValue.datePeriod === 146 || this.relativeValue.datePeriod === 147) {
                    return [
                        { text: 'week', value: 'weeks' },
                        { text: 'month', value: 'months' },
                        { text: 'quarter', value: 'quarters' },
                        { text: 'year', value: 'years' },
                    ];
                }
                if (this.relativeValue.datePeriod === 148) {
                    return [
                        { text: 'January', value: 1 },
                        { text: 'February', value: 2 },
                        { text: 'March', value: 3 },
                        { text: 'April', value: 4 },
                        { text: 'May', value: 5 },
                        { text: 'June', value: 6 },
                        { text: 'July', value: 7 },
                        { text: 'August', value: 8 },
                        { text: 'September', value: 9 },
                        { text: 'October', value: 10 },
                        { text: 'November', value: 11 },
                        { text: 'December', value: 12 },
                    ];
                }
                if (this.relativeValue.datePeriod === 149) {
                    return [
                        { text: 'week(s)', value: 'weeks' },
                    ];
                }
            }
            return [];
        },
        dateValue: {
            get() {
                if (this.config.allowRelativeDate) {
                    let tmpValue = library.isJson(this.value) ? JSON.parse(this.value) : this.value;
                    if (typeof tmpValue?.datePeriod !== 'undefined') {
                        return '';
                    }
                }
                if (this.config.saveAsObject) return this.value;
                if (this.value && moment(this.value)._isValid) return moment(this.value)._d;
                return '';
            },
            set(value) {
                if (this.config.saveAsObject) this.value = value;
                else if (!this.value || (this.value && this.value !== moment(value).format('YYYY-MM-DD'))) {
                    this.value = value ? moment(value).format('YYYY-MM-DD') : null;
                }
            }
        },
        showDayInput() {
            let showDayInput = [148, 149];
            return showDayInput.indexOf(this.relativeValue.datePeriod) !== -1;
        },
        showDateInput() {
            let showDateInput = [0];
            return !this.config.allowRelativeDate || showDateInput.indexOf(this.relativeValue.datePeriod) !== -1;
        },
        showAmountInput() {
            let showAmountInput = [90, 91, 149];
            return showAmountInput.indexOf(this.relativeValue.datePeriod) !== -1;
        },
        showUnitInput() {
            let showUnitInput = [90, 91, 146, 147, 148, 149];
            return showUnitInput.indexOf(this.relativeValue.datePeriod) !== -1;
        },
        relativeValue: {
            get() {
                let tmpValue = library.isJson(this.value) ? JSON.parse(this.value) : this.value;
                if (typeof tmpValue?.datePeriod !== 'undefined') {
                    this.localRelativeValue = library.cloneObj(tmpValue);
                }
                return this.localRelativeValue;
            },
            set(value) {
                this.localRelativeValue = value;
            }
        },
        ref() {
            return this.config.ref || this.config.id;
        }
    },
    watch: {
        value(newValue) {
            if (this.value !== newValue) this.value = newValue;
        }
    },
    components: {
        'ejs-datepicker': defineAsyncComponent(() => import('@common/syncfusion/datePicker')),
        'ejs-calendar': defineAsyncComponent(() => import('@common/syncfusion/calendar')),
        'ejs-dropdownlist': defineAsyncComponent(() => import('@common/syncfusion/dropdownList')),
        'ejs-numerictextbox': defineAsyncComponent(() => import('@common/syncfusion/numeric')),
    }
};

export default dateComponent;
</script>

<style scoped>
.date-calendar > div {
    margin: 0 auto;
}
:deep(.flex) {
    display: flex;
}
:deep(.flex > *) {
    width: 200px;
    margin-right: 10px;
}
.empty-field-notice {
    position: relative;
    top: -25px;
    margin: 0;
    height: 0;
    overflow: visible;
    font-size: 14px;
}
.empty-field-notice a{
    cursor: pointer;
}
</style>
